import React, { useState, useEffect } from "react";
import {
  X,
  Save,
  Loader2,
  UserCheck,
  Search,
  Info,
  Users,
  AlertTriangle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  getAttendanceSheet,
  saveAttendanceSheet,
} from "../../../api/elearning/attendance/attendance";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import { todayLocalISODate } from "../../../utils/formatUtils";
import {
  ATTENDANCE_STATUS_BTNS,
  normalizeAttendanceStatus,
  isAbsentStatus,
  countAbsentStatuses,
} from "./attendanceStatus";

const AttendanceSheetModal = ({ isOpen, onClose, classInfo, date, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [attendanceList, setAttendanceList] = useState([]);
  const [noteMap, setNoteMap] = useState({});
  const [canEdit, setCanEdit] = useState(true);
  const [isTodayDate, setIsTodayDate] = useState(true);
  const [requiresEditReason, setRequiresEditReason] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [editWindowDays, setEditWindowDays] = useState(7);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !classInfo?.id) return;
    setSearchTerm("");
    setEditReason("");
    setFetching(true);
    const checkDate = date || todayLocalISODate();
    const today = todayLocalISODate();
    const dateIsToday = checkDate === today;
    setIsTodayDate(dateIsToday);
    getAttendanceSheet(classInfo.id, checkDate)
      .then((data) => {
        const records = (data?.records || []).map((r) => ({
          student_id: r.student_id,
          full_name: r.full_name,
          parent_name: r.parent_name,
          parent_phone: r.parent_phone,
          status: normalizeAttendanceStatus(r.status),
          teacher_note: r.teacher_note || "",
          staff_remark: r.staff_remark || "",
        }));
        setAttendanceList(records);
        const map = {};
        records.forEach((r) => {
          map[r.student_id] = r.teacher_note;
        });
        setNoteMap(map);
        setEditReason(data?.edit_reason || "");
        setRequiresEditReason(Boolean(data?.requires_edit_reason));
        setEditWindowDays(Number(data?.edit_window_days) || 7);
        setIsTodayDate(
          typeof data?.is_today === "boolean" ? data.is_today : dateIsToday
        );
        setCanEdit(typeof data?.can_edit === "boolean" ? data.can_edit : true);
      })
      .catch(() => {
        showErrorToast("Không thể tải danh sách học viên");
        setAttendanceList([]);
        setCanEdit(false);
      })
      .finally(() => setFetching(false));
  }, [isOpen, classInfo?.id, date]);

  const handleStatusChange = (studentId, newStatus) => {
    if (!canEdit) return;
    setAttendanceList((prev) =>
      prev.map((item) =>
        item.student_id === studentId ? { ...item, status: newStatus } : item
      )
    );
  };

  const handleMarkAllPresent = () => {
    if (!canEdit) return;
    setAttendanceList((prev) =>
      prev.map((item) => ({ ...item, status: "Present" }))
    );
  };

  const handleNoteChange = (studentId, value) => {
    if (!canEdit) return;
    setNoteMap((prev) => ({ ...prev, [studentId]: value }));
  };

  const handleSave = async () => {
    if (!classInfo?.id) return;
    if (!canEdit) {
      showErrorToast(
        !isTodayDate
          ? `Chỉ có thể cập nhật trong vòng ${editWindowDays} ngày (kèm lý do) hoặc liên hệ quản trị.`
          : "Chỉ giáo viên phụ trách lớp hoặc Super Admin mới được lưu điểm danh."
      );
      return;
    }
    if (requiresEditReason && !String(editReason || "").trim()) {
      showErrorToast("Vui lòng nhập lý do điểm danh bù / cập nhật.");
      return;
    }
    setLoading(true);
    try {
      const checkDate = date || todayLocalISODate();
      const records = attendanceList.map((r) => ({
        student_id: r.student_id,
        status: r.status,
        teacher_note: noteMap[r.student_id] ?? r.teacher_note ?? "",
      }));
      await saveAttendanceSheet({
        class_id: classInfo.id,
        date: checkDate,
        records,
        edit_reason: requiresEditReason ? editReason.trim() : undefined,
      });
      showSuccessToast("Đã lưu điểm danh thành công");
      onSaved?.();
      onClose?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Không thể lưu điểm danh");
    } finally {
      setLoading(false);
    }
  };

  const filteredList = attendanceList.filter((s) =>
    s.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const presentCount = attendanceList.filter((s) => s.status === "Present").length;
  const lateCount = attendanceList.filter((s) => s.status === "Late").length;
  const absentCount = countAbsentStatuses(attendanceList);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[999] flex items-end md:items-center justify-center overflow-hidden">
          <motion.div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="relative z-10 w-full max-w-4xl h-[min(96dvh,100%)] md:h-auto md:max-h-[90vh] flex flex-col pointer-events-none"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
          >
            <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full flex flex-col overflow-hidden pointer-events-auto border border-white max-h-full">
              <div className="md:hidden flex justify-center pt-2 pb-0.5 shrink-0">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>

              <div className="bg-white px-3.5 md:px-6 py-2.5 md:py-4 flex justify-between items-start gap-2 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-red-600 rounded-xl flex items-center justify-center shadow-md shadow-red-100 text-white shrink-0">
                    <UserCheck className="w-4.5 h-4.5 md:w-5 md:h-5" size={18} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-[15px] md:text-lg font-bold text-gray-900 leading-tight tracking-tight truncate">
                      {classInfo?.name || "Điểm danh lớp"}
                    </h2>
                    <p className="text-[11px] md:text-sm text-gray-400 mt-0.5 font-medium truncate">
                      {date || todayLocalISODate()}
                      {classInfo?.venue_label ? ` · ${classInfo.venue_label}` : ""}
                      {attendanceList.length ? ` · ${attendanceList.length} HV` : ""}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2.5 -mr-1 hover:bg-gray-100 rounded-xl transition-colors text-gray-400 shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Đóng"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="px-3.5 md:px-6 py-2.5 md:py-3 bg-gray-50/60 border-b border-gray-100 space-y-2 shrink-0">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      size={15}
                    />
                    <input
                      type="search"
                      enterKeyHint="search"
                      placeholder="Tìm tên học viên..."
                      value={searchTerm}
                      className="w-full bg-white border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-base md:text-sm outline-none focus:border-red-400 font-medium"
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  {canEdit && attendanceList.length > 0 ? (
                    <button
                      type="button"
                      onClick={handleMarkAllPresent}
                      className="shrink-0 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 text-[11px] font-bold whitespace-nowrap active:scale-95 transition-all"
                      title="Đánh dấu tất cả có mặt"
                    >
                      Tất cả có mặt
                    </button>
                  ) : null}
                </div>

                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    {
                      label: "Có mặt",
                      count: presentCount,
                      tone: "bg-emerald-50 text-emerald-700 border-emerald-100",
                    },
                    {
                      label: "Muộn",
                      count: lateCount,
                      tone: "bg-orange-50 text-orange-700 border-orange-100",
                    },
                    {
                      label: "Vắng",
                      count: absentCount,
                      tone: "bg-red-50 text-red-600 border-red-100",
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className={`rounded-lg border px-2 py-1.5 text-center ${s.tone}`}
                    >
                      <p className="text-sm font-black leading-none">{s.count}</p>
                      <p className="text-[9px] font-bold uppercase tracking-wide mt-0.5 opacity-80">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>

                {requiresEditReason && canEdit ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle size={15} />
                      <p className="text-[11px] font-bold">Điểm danh bù — bắt buộc ghi lý do</p>
                    </div>
                    <textarea
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      placeholder="Nhập lý do cập nhật (học bù, bổ sung sau buổi học...)"
                      rows={2}
                      className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-400"
                    />
                  </div>
                ) : null}

                {!canEdit ? (
                  <p className="text-center text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5">
                    {isTodayDate
                      ? "Bạn không có quyền chỉnh sửa điểm danh lớp này."
                      : `Ngày quá khứ — chỉ xem hoặc cập nhật trong ${editWindowDays} ngày (GV phụ trách).`}
                  </p>
                ) : !isTodayDate ? (
                  <p className="text-center text-[11px] font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-lg px-3 py-1.5">
                    Đang cập nhật điểm danh bù — có thể sửa trong {editWindowDays} ngày kể từ ngày buổi học.
                  </p>
                ) : null}
              </div>

              <div
                className="flex-1 overflow-y-auto overscroll-contain bg-slate-50/40 p-2.5 md:p-4"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                {fetching && (
                  <div className="flex justify-center py-10">
                    <Loader2 className="animate-spin text-red-500" size={26} />
                  </div>
                )}
                {!fetching && filteredList.length === 0 && (
                  <div className="text-center py-10 px-4">
                    <Users className="mx-auto text-gray-300 mb-2" size={28} />
                    <p className="text-gray-500 text-sm font-medium">
                      {attendanceList.length === 0
                        ? "Lớp chưa có học viên đăng ký."
                        : "Không tìm thấy học viên."}
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pb-2">
                  {!fetching &&
                    filteredList.map((student, idx) => (
                      <div
                        key={student.student_id}
                        className={`p-3 rounded-xl border transition-all ${
                          isAbsentStatus(student.status)
                            ? student.status === "AbsentExcused"
                              ? "bg-sky-50 border-sky-100"
                              : "bg-red-50 border-red-100"
                            : student.status === "Late"
                              ? "bg-orange-50/50 border-orange-100"
                              : "bg-white border-gray-100"
                        }`}
                      >
                        <div className="flex items-start gap-2 mb-2.5">
                          <span className="mt-0.5 text-[10px] font-bold text-gray-300 w-4 shrink-0 tabular-nums">
                            {idx + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p
                              className={`text-[15px] md:text-sm font-bold leading-snug ${
                                isAbsentStatus(student.status)
                                  ? student.status === "AbsentExcused"
                                    ? "text-sky-700"
                                    : "text-red-700"
                                  : "text-gray-800"
                              }`}
                            >
                              {student.full_name}
                            </p>
                            <input
                              type="text"
                              placeholder="Ghi chú giáo viên..."
                              value={
                                noteMap[student.student_id] ??
                                student.teacher_note ??
                                ""
                              }
                              disabled={!canEdit}
                              className="mt-1 w-full text-[13px] md:text-[11px] bg-white/70 md:bg-transparent border border-gray-100 md:border-none rounded-lg md:rounded-none px-2 py-1.5 md:px-0 md:py-0 outline-none text-gray-500 focus:text-gray-700 focus:border-red-200 font-medium disabled:cursor-not-allowed disabled:opacity-70"
                              onChange={(e) =>
                                handleNoteChange(student.student_id, e.target.value)
                              }
                            />
                          </div>
                        </div>

                        <div
                          className={`grid grid-cols-4 gap-1 p-1 rounded-xl ${
                            canEdit ? "bg-gray-100/90" : "bg-gray-50 opacity-80"
                          }`}
                        >
                          {ATTENDANCE_STATUS_BTNS.map((btn) => {
                            const active = student.status === btn.val;
                            return (
                              <button
                                key={btn.val}
                                type="button"
                                disabled={!canEdit}
                                onClick={() =>
                                  handleStatusChange(student.student_id, btn.val)
                                }
                                className={`flex flex-col items-center justify-center gap-0.5 min-h-[48px] md:min-h-0 md:py-2 rounded-lg transition-all disabled:opacity-60 ${
                                  active
                                    ? `${btn.active} ring-1 ${btn.ring}`
                                    : "text-gray-400 active:bg-white/60"
                                }`}
                              >
                                <btn.icon className="w-4 h-4 md:w-[16px] md:h-[16px]" />
                                <span className="text-[9px] md:text-[10px] font-bold">
                                  {btn.shortLabel}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className="bg-white px-3.5 md:px-6 pt-3 pb-[max(0.85rem,env(safe-area-inset-bottom))] md:py-4 flex flex-col md:flex-row justify-between items-stretch md:items-center border-t border-gray-100 shrink-0 gap-2.5 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]">
                <div className="hidden md:flex items-center gap-2 text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
                  <Info size={14} />
                  <span className="text-[11px] font-bold">
                    Có thể cập nhật trong {editWindowDays} ngày (kèm lý do)
                  </span>
                </div>

                <div className="flex w-full md:w-auto gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 md:flex-none min-h-[48px] md:min-h-0 px-5 py-3 md:py-2.5 rounded-xl font-bold text-gray-500 bg-gray-50 md:bg-transparent hover:bg-gray-100 transition-all text-sm md:text-xs"
                  >
                    Đóng
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={
                      !canEdit || loading || fetching || attendanceList.length === 0
                    }
                    className="flex-[2] md:flex-none min-h-[48px] md:min-h-0 px-6 py-3 md:py-2.5 bg-red-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-red-700 shadow-sm transition-all active:scale-[0.98] disabled:opacity-50 text-sm md:text-xs"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Save size={18} />
                    )}
                    {loading ? "Đang lưu..." : "Lưu điểm danh"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AttendanceSheetModal;
